import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  css`
    /* ── Host ───────────────────────────────────────────────────────────────── */
    :host {
      display: block;
      position: relative;

      /* Single source of truth — cascade into all panels in this shadow root */
      --_mb-bg:    var(--uwc-menubar-bg,    ${t.surface});
      --_mb-color: var(--uwc-menubar-color, ${t.text});
    }

    /* ── Bar container ──────────────────────────────────────────────────────── */
    .uwc-mbar {
      display: flex;
      align-items: center;
      gap: var(--uwc-menubar-gap, ${t.space1});
      min-height: var(--uwc-menubar-height, calc(${t.controlHeightMd} + ${t.space3}));
      padding: var(--uwc-menubar-padding, ${t.space2} ${t.space4});
      background: var(--_mb-bg);
      color: var(--_mb-color);
      border: var(--uwc-menubar-border, 1px solid ${t.border});
      border-radius: var(--uwc-menubar-border-radius, ${t.radiusMd});
      box-shadow: var(--uwc-menubar-shadow, none);
      position: relative;
    }

    /* ── Start / End slots ──────────────────────────────────────────────────── */
    .uwc-mbar__start {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      margin-inline-end: ${t.space2};
    }
    .uwc-mbar__start:empty { display: none; }

    .uwc-mbar__end {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      margin-inline-start: auto;
    }
    .uwc-mbar__end:empty { display: none; }

    /* ── Desktop nav list ───────────────────────────────────────────────────── */
    .uwc-mbar__list {
      display: flex;
      align-items: center;
      list-style: none;
      margin: 0;
      padding: 0;
      gap: var(--uwc-menubar-gap, ${t.space1});
      flex: 1;
      justify-content: var(--uwc-menubar-list-justify, flex-start);
    }

    /* menu-align attribute overrides */
    :host([menu-align="center"]) .uwc-mbar__list { justify-content: center; }
    :host([menu-align="end"])    .uwc-mbar__list { justify-content: flex-end; }

    /* ── Top-level item ─────────────────────────────────────────────────────── */
    .uwc-mbar__item {
      position: relative;
    }

    /* ── Top-level item button ──────────────────────────────────────────────── */
    .uwc-mbar__item-btn {
      display: inline-flex;
      align-items: center;
      gap: ${t.space2};
      padding: var(--uwc-menubar-item-padding, ${t.space2} ${t.space3});
      font-size: var(--uwc-menubar-item-font-size, ${t.fontSizeMd});
      font-weight: var(--uwc-menubar-item-font-weight, ${t.fontWeightMedium});
      font-family: var(--uwc-font-family, inherit);
      color: inherit;
      background: transparent;
      border: none;
      border-radius: var(--uwc-menubar-item-radius, ${t.radiusMd});
      cursor: pointer;
      white-space: nowrap;
      text-align: left;
      line-height: 1.4;
      transition:
        background ${t.durationFast} ${t.easingStandard},
        color       ${t.durationFast} ${t.easingStandard};
    }
    .uwc-mbar__item-btn:hover,
    .uwc-mbar__item-btn:focus-visible {
      background: var(--uwc-menubar-item-hover-bg,
        color-mix(in oklab, var(--_mb-color) 8%, transparent));
      outline: none;
    }
    .uwc-mbar__item-btn:focus-visible {
      outline: 2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: 2px;
    }
    .uwc-mbar__item-btn[aria-expanded="true"],
    .uwc-mbar__item--open > .uwc-mbar__item-btn {
      background: var(--uwc-menubar-item-active-bg, ${t.selectedBg});
    }
    .uwc-mbar__item--selected > .uwc-mbar__item-btn {
      background: var(--uwc-menubar-item-active-bg, ${t.selectedBg});
      color: var(--uwc-color-primary, ${t.primary});
    }
    .uwc-mbar__item-btn[disabled] {
      opacity: 0.45;
      cursor: not-allowed;
      pointer-events: none;
    }

    /* ── Item icon ──────────────────────────────────────────────────────────── */
    .uwc-mbar__item-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      flex-shrink: 0;
    }

    /* ── Chevrons ───────────────────────────────────────────────────────────── */
    .uwc-mbar__chevron {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 14px;
      height: 14px;
      flex-shrink: 0;
      color: color-mix(in oklab, var(--_mb-color) 50%, transparent);
      transition: transform ${t.durationBase} ${t.easingStandard};
    }
    .uwc-mbar__item-btn[aria-expanded="true"] .uwc-mbar__chevron {
      transform: rotate(180deg);
    }

    /* ── Dropdown panel (top-level submenu) — lives in top layer via popover ── */
    .uwc-mbar__panel {
      /* position/inset overridden by inline styles set from getBoundingClientRect() */
      position: fixed;
      inset: 0 auto auto 0;
      margin: 0;
      list-style: none;
      padding: var(--uwc-menubar-panel-padding, ${t.space1} 0);
      min-width: var(--uwc-menubar-panel-min-width, 180px);
      background: var(--uwc-menubar-panel-bg, var(--_mb-bg));
      color: var(--uwc-menubar-panel-color, var(--_mb-color));
      border: var(--uwc-menubar-panel-border, 1px solid ${t.border});
      border-radius: var(--uwc-menubar-panel-radius, ${t.radiusMd});
      box-shadow: var(--uwc-menubar-panel-shadow, ${t.shadowLg});
      z-index: var(--uwc-menubar-z, ${t.zFloat});
      /* entry animation driven by :popover-open */
      opacity: 0;
      transform: scale(0.97) translateY(-4px);
      transition:
        opacity   ${t.durationBase} ${t.easingStandard},
        transform ${t.durationBase} ${t.easingStandard},
        display   ${t.durationBase} allow-discrete,
        overlay   ${t.durationBase} allow-discrete;
    }
    .uwc-mbar__panel:popover-open {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
    @starting-style {
      .uwc-mbar__panel:popover-open {
        opacity: 0;
        transform: scale(0.97) translateY(-4px);
      }
    }

    /* ── Nested submenu panel — own top-layer via popover, position:fixed ───── */
    .uwc-mbar__panel-li { position: relative; }
    .uwc-mbar__panel--nested {
      transform-origin: left center;
    }
    .uwc-mbar__panel--nested:popover-open {
      transform: scale(1) translateX(0);
    }
    @starting-style {
      .uwc-mbar__panel--nested:popover-open {
        opacity: 0;
        transform: scale(0.97) translateX(-4px);
      }
    }

    /* ── Panel item (shared by all depth levels) ────────────────────────────── */
    .uwc-mbar__panel-item {
      display: flex;
      align-items: center;
      gap: 9px;
      width: 100%;
      padding: var(--uwc-menubar-panel-item-padding, ${t.space2} ${t.space4});
      font-size: var(--uwc-menubar-item-font-size, ${t.fontSizeMd});
      font-weight: var(--uwc-menubar-item-font-weight, ${t.fontWeightMedium});
      font-family: var(--uwc-font-family, inherit);
      color: inherit;
      background: transparent;
      border: none;
      border-radius: 0;
      cursor: pointer;
      white-space: nowrap;
      text-align: left;
      text-decoration: none;
      line-height: 1.4;
      transition:
        background ${t.durationFast} ${t.easingStandard},
        color       ${t.durationFast} ${t.easingStandard};
    }
    .uwc-mbar__panel-item:hover,
    .uwc-mbar__panel-item:focus-visible {
      background: var(--uwc-menubar-panel-item-hover-bg,
        color-mix(in oklab, var(--_mb-color) 10%, transparent));
      outline: none;
    }
    .uwc-mbar__panel-item:focus-visible {
      outline: 2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: -2px;
    }
    .uwc-mbar__panel-item[disabled],
    .uwc-mbar__panel-li--disabled .uwc-mbar__panel-item {
      opacity: 0.45;
      cursor: not-allowed;
      pointer-events: none;
    }
    .uwc-mbar__panel-li--active > .uwc-mbar__panel-item,
    .uwc-mbar__panel-li--open   > .uwc-mbar__panel-item {
      background: color-mix(in oklab, var(--_mb-color) 8%, transparent);
    }
    .uwc-mbar__panel-li--danger > .uwc-mbar__panel-item {
      color: var(--uwc-menubar-panel-danger-color, ${t.danger});
    }
    .uwc-mbar__panel-li--danger > .uwc-mbar__panel-item:hover {
      background: color-mix(in oklab, var(--uwc-menubar-panel-danger-color, ${t.danger}) 10%, transparent);
    }

    /* ── Panel item sub-elements ────────────────────────────────────────────── */
    .uwc-mbar__panel-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      flex-shrink: 0;
    }
    .uwc-mbar__panel-label {
      flex: 1;
    }
    .uwc-mbar__panel-badge {
      font-size: ${t.fontSizeXs};
      padding: 1px 6px;
      border-radius: ${t.radiusFull};
      background: ${t.borderSubtle};
      color: ${t.textSecondary};
      font-weight: ${t.fontWeightMedium};
      flex-shrink: 0;
    }

    /* ── Separator ──────────────────────────────────────────────────────────── */
    .uwc-mbar__separator {
      height: 1px;
      background: var(--uwc-menubar-panel-separator-color, ${t.border});
      margin: ${t.space1} 0;
    }

    /* ── Hamburger button (mobile only) ─────────────────────────────────────── */
    .uwc-mbar__hamburger {
      display: none;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      background: transparent;
      border: none;
      border-radius: var(--uwc-menubar-item-radius, ${t.radiusMd});
      cursor: pointer;
      color: inherit;
      flex-shrink: 0;
      transition: background ${t.durationFast} ${t.easingStandard};
    }
    .uwc-mbar__hamburger:hover {
      background: color-mix(in oklab, var(--_mb-color) 8%, transparent);
    }
    .uwc-mbar__hamburger:focus-visible {
      outline: 2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: 2px;
    }

    /* ── Mobile panel — top-layer via popover, position:fixed set via JS ───── */
    .uwc-mbar__mobile-panel {
      position: fixed;
      inset: 0 auto auto 0;
      margin: 0;
      /* top/left/width set via JS to viewport-full width (left:0, width:100vw) */
      background: var(--uwc-menubar-panel-bg, var(--_mb-bg));
      color: var(--uwc-menubar-panel-color, var(--_mb-color));
      border: var(--uwc-menubar-panel-border, 1px solid ${t.border});
      border-radius: var(--uwc-menubar-panel-radius, ${t.radiusMd});
      box-shadow: var(--uwc-menubar-panel-shadow, ${t.shadowLg});
      z-index: var(--uwc-menubar-z, ${t.zFloat});
      padding: ${t.space2} 0;
      overflow: auto;
      max-height: calc(100svh - 5rem);
      /* entry / exit driven by :popover-open */
      opacity: 0;
      transform: scale(0.97) translateY(-4px);
      transition:
        opacity   ${t.durationBase} ${t.easingStandard},
        transform ${t.durationBase} ${t.easingStandard},
        display   ${t.durationBase} allow-discrete,
        overlay   ${t.durationBase} allow-discrete;
    }
    .uwc-mbar__mobile-panel:popover-open {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
    @starting-style {
      .uwc-mbar__mobile-panel:popover-open {
        opacity: 0;
        transform: scale(0.97) translateY(-4px);
      }
    }

    .uwc-mbar__mobile-list {
      list-style: none;
      margin: 0;
      padding: 0;
    }

    /* Mobile nested indent */
    .uwc-mbar__mobile-panel .uwc-mbar__panel--nested {
      position: static;
      opacity: 1;
      transform: none;
      transition: none;
      animation: none;
      box-shadow: none;
      border: none;
      border-radius: 0;
      background: color-mix(in oklab, var(--_mb-color) 4%, transparent);
      padding-inline-start: ${t.space4};
      min-width: 0;
    }

    /* ── Animations ─────────────────────────────────────────────────────────── */
    @keyframes uwc-mbar-nested-in {
      from { opacity: 0; transform: scale(0.97) translateX(-4px); }
      to   { opacity: 1; transform: scale(1)    translateX(0);    }
    }

    /* ── Reduced motion ─────────────────────────────────────────────────────── */
    @media (prefers-reduced-motion: reduce) {
      .uwc-mbar__panel,
      .uwc-mbar__mobile-panel {
        transition: none;
      }
      .uwc-mbar__panel--nested {
        animation: none;
      }
      .uwc-mbar__item-btn,
      .uwc-mbar__panel-item,
      .uwc-mbar__hamburger,
      .uwc-mbar__chevron {
        transition: none;
      }
    }

    /* ── Responsive — mobile (<768px) ───────────────────────────────────────── */
    @media (max-width: 767px) {
      .uwc-mbar__list    { display: none; }
      .uwc-mbar__hamburger { display: inline-flex; }
    }

    /* ── Responsive — desktop (≥768px) ─────────────────────────────────────── */
    @media (min-width: 768px) {
      .uwc-mbar__hamburger       { display: none !important; }
      .uwc-mbar__mobile-panel    { display: none !important; }
    }

    /* ── compact — hides the horizontal nav list ────────────────────────────── */
    :host([compact]) .uwc-mbar__list { display: none !important; }
  `,
];
